import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  LogIn,
  UserPlus,
  Layers,
  Crosshair,
  ShieldCheck,
  CalendarClock,
  Zap,
} from 'lucide-react';
import PublicTopNav from '../components/layout/PublicTopNav';
import PublicFooter from '../components/layout/PublicFooter';
import ScreenshotFrame from '../components/landing/ScreenshotFrame';
import { ZEBU_SIGNUP_URL } from '../config/tenantConfig';

/**
 * Marketing / product page for the no-code strategy builder. Reached from every
 * "Build Your Own Algo" CTA when the visitor is NOT logged in (logged-in users
 * go straight to /baskets). Funnels prospects to Login (existing account) or
 * Open Demat Account (new users → ZEBU_SIGNUP_URL).
 *
 * Screenshots are loaded from /public/screenshots/*. Until those files exist,
 * ScreenshotFrame shows a labeled placeholder — drop the images in to go live.
 */
const BuildYourOwnPage: React.FC = () => {
  // Primary actions, reused in the hero and the closing CTA band.
  const LoginButton: React.FC<{ className?: string }> = ({ className }) => (
    <Link
      to="/auth"
      className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-zebu-600 hover:bg-zebu-700 text-white font-semibold shadow-lg shadow-zebu-600/20 hover:shadow-xl transition-all ${className ?? ''}`}
    >
      <LogIn className="h-4 w-4" /> Login
    </Link>
  );

  const OpenDematButton: React.FC<{ className?: string }> = ({ className }) => (
    <a
      href={ZEBU_SIGNUP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-zebu-200 dark:border-zebu-800 text-zebu-700 dark:text-zebu-300 font-semibold hover:bg-zebu-50 dark:hover:bg-zebu-900/30 transition-all ${className ?? ''}`}
    >
      <UserPlus className="h-4 w-4" /> Open Demat Account
    </a>
  );

  // Full-width product-tour blocks, each backed by a real platform screenshot
  // in /public/screenshots. `portrait` constrains the frame for tall shots so
  // they don't stretch across the full container.
  const showcase = [
    {
      eyebrow: 'Strategy Builder',
      title: 'Design multi-leg strategies, visually',
      desc:
        'Add CE/PE legs, choose buy or sell, set per-leg lots, and attach stop-loss, target, and trailing rules — all without writing a line of code.',
      srcDark: '/screenshots/strategy-editor-dark.png',
      srcLight: '/screenshots/strategy-editor-light.png',
      alt: 'Multi-leg strategy editor with legs, schedule and risk',
      caption: 'strategies · builder',
      portrait: false,
      disclaimer:
        'Disclaimer: Sample strategy configuration shown for demonstration purposes only and should not be construed as investment advice or a recommendation to trade.',
    },
    {
      eyebrow: 'Live Execution',
      title: 'Monitor every position in real time',
      desc:
        'Track live P&L, fills, and stop-loss status across brokers and strategies — and exit any position in one click.',
      srcDark: '/screenshots/live-positions-dark.png',
      srcLight: '/screenshots/live-positions-light.png',
      alt: 'Live positions dashboard with P&L and stop-loss status',
      caption: 'live · positions',
      portrait: false,
      disclaimer:
        'Disclaimer: Sample data shown for demonstration purposes only and should not be construed as investment advice or a recommendation to trade.',
    },
    {
      eyebrow: 'Baskets',
      title: 'Organize strategies into baskets',
      desc:
        'Group related strategies, set starting capital, and configure multi-broker allocations for clean, scalable execution.',
      srcDark: '/screenshots/basket-create-dark.png',
      srcLight: '/screenshots/basket-create-light.png',
      alt: 'Create new basket dialog',
      caption: 'baskets · create',
      portrait: true,
      disclaimer:
        'Disclaimer: Sample strategy configuration shown for demonstration purposes only and should not be construed as investment advice or a recommendation to trade.',
    },
  ];

  const features = [
    { icon: Layers, title: 'Multi-leg strategies', desc: 'CE/PE, buy & sell, per-leg lots.' },
    { icon: Crosshair, title: 'Smart strike selection', desc: 'By premium, ATM, or straddle.' },
    { icon: ShieldCheck, title: 'Risk management', desc: 'SL, target, trailing & re-entry.' },
    { icon: CalendarClock, title: 'Time-based scheduling', desc: 'Entries & exits by weekday.' },
    { icon: Zap, title: 'One-click deploy', desc: 'Automate on your own account.' },
  ];


  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
      <PublicTopNav />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-zebu-50/70 via-white to-white dark:from-gray-900 dark:via-gray-950 dark:to-gray-950">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-zebu-200/30 dark:bg-zebu-900/20 rounded-full blur-3xl -z-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 text-xs font-semibold uppercase tracking-wider">
                No-Code Strategy Builder
              </span>
              <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-[1.1]">
                Build Your Own
                <br />
                Algo Strategy
              </h1>
              <p className="mt-5 text-lg text-gray-600 dark:text-gray-300 max-w-lg">
                Design and automate your own multi-leg option strategies on Zebu —
                from idea to live execution in minutes. No coding required.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <LoginButton />
                <OpenDematButton />
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Already have a Zebu account? <span className="font-medium text-gray-700 dark:text-gray-300">Log in</span> to start building.
                New to Zebu? Open a free Demat account in minutes.
              </p>
            </div>

            <div className="relative">
              <ScreenshotFrame
                srcDark="/screenshots/strategy-builder-dark.png"
                srcLight="/screenshots/strategy-builder-light.png"
                alt="Zebu no-code strategy builder"
                caption="zebu · new strategy"
                disclaimer="Disclaimer: Sample strategy configuration shown for demonstration purposes only and should not be construed as investment advice or a recommendation to trade."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-2xl font-bold text-center mb-3">Everything you need to build and automate</h2>
        <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-10 max-w-xl mx-auto">
          A complete toolkit for designing, validating, and running your own strategies.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5"
            >
              <div className="h-10 w-10 rounded-lg bg-zebu-50 dark:bg-zebu-900/40 flex items-center justify-center mb-3">
                <f.icon className="h-5 w-5 text-zebu-600 dark:text-zebu-400" />
              </div>
              <h3 className="font-semibold mb-1">{f.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product tour — full-width screenshot blocks */}
      <section className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {showcase.map((item) => (
            <div key={item.title}>
              <div className="text-center max-w-2xl mx-auto mb-8">
                <span className="text-xs font-semibold uppercase tracking-wider text-zebu-600 dark:text-zebu-400">
                  {item.eyebrow}
                </span>
                <h3 className="mt-2 text-2xl font-bold">{item.title}</h3>
                <p className="mt-3 text-gray-600 dark:text-gray-400">{item.desc}</p>
              </div>
              <ScreenshotFrame
                srcDark={item.srcDark}
                srcLight={item.srcLight}
                alt={item.alt}
                caption={item.caption}
                disclaimer={item.disclaimer}
                className={item.portrait ? 'max-w-md mx-auto' : ''}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-gradient-to-br from-zebu-700 to-zebu-600 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Ready to build your first algo?</h2>
          <p className="mt-3 text-zebu-100 max-w-lg mx-auto">
            Log in to start building, or open a free Demat account to get started with Zebu.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/auth"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-zebu-700 font-semibold shadow-lg hover:bg-zebu-50 transition-all"
            >
              <LogIn className="h-4 w-4" /> Login
            </Link>
            <a
              href={ZEBU_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition-all"
            >
              <UserPlus className="h-4 w-4" /> Open Demat Account <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default BuildYourOwnPage;
